/**
 * "Your data" — export and erasure (M1 exit criteria, ADR-026).
 *
 * Both operations existed as promises before they existed as code:
 * `deleteAccount()` sat in auth.js with nothing calling it, and there was no
 * export at all. A deletion nobody can reach is not a deletion.
 *
 * ── Why erasure needed a rules change (ADR-039) ────────────────────────────
 *
 * Assessments were create-only: `allow update, delete: if false`. Append-only
 * is a real integrity property — the delta over time is the product, and a
 * history that can be quietly rewritten is not a history (R13, ADR-022).
 *
 * But it also made the deletion promise false. On the Spark plan there is no
 * Cloud Function to clean up after an auth deletion, so deleting the Firebase
 * user left every assessment document sitting in Firestore forever, merely
 * unreachable — that is abandonment, not erasure.
 *
 * The resolution is that immutability guards against REWRITING history, not
 * against the person whose history it is choosing to end it. `update` stays
 * closed. `delete` opens to the owner alone. The client therefore erases in
 * this order, which is the only order that works:
 *
 *   1. assessment documents   (needs auth — the rules key on request.auth.uid)
 *   2. the user document
 *   3. the Firebase Auth user
 *
 * Reverse any two of those and the credentials that authorise the deletion are
 * gone before the data is.
 */
import {
  doc, getDoc, getDocs, collection, deleteDoc, query, orderBy,
} from "firebase/firestore";
import { db, auth } from "./firebase";

/** Firestore Timestamps do not survive JSON.stringify in any useful form. */
const iso = (v) =>
  v && typeof v.toDate === "function" ? v.toDate().toISOString()
  : v === undefined ? null
  : v;

/** Recursively normalise Timestamps so the export is readable, not `{}`. */
const plain = (v) => {
  if (v === null || typeof v !== "object") return v ?? null;
  if (typeof v.toDate === "function") return iso(v);
  if (Array.isArray(v)) return v.map(plain);
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, plain(x)]));
};

/**
 * Everything we hold on one person, as a plain object.
 *
 * Deliberately built by READING the collections rather than from a hand-written
 * field list: an export that enumerates fields goes stale the moment someone
 * adds one, and a stale export is a false answer to "what do you have on me".
 */
export async function exportAccount(uid) {
  if (!db || !uid) return { ok: false, reason: "no-db" };
  try {
    const profileSnap = await getDoc(doc(db, "users", uid));
    const assessmentsSnap = await getDocs(
      query(collection(db, "users", uid, "assessments"), orderBy("at", "asc"))
    );
    return {
      ok: true,
      data: {
        exportedAt: new Date().toISOString(),
        note:
          "Everything SignalPath holds for this account. Résumés are parsed in " +
          "your browser and never uploaded, so no résumé appears here — because " +
          "none was ever sent (ADR-016).",
        account: { uid, email: auth?.currentUser?.email ?? null },
        profile: profileSnap.exists() ? plain(profileSnap.data()) : null,
        assessments: assessmentsSnap.docs.map((d) => ({ id: d.id, ...plain(d.data()) })),
      },
    };
  } catch (e) {
    console.error("[account] exportAccount", e?.code || e);
    return { ok: false, reason: e?.code || "read-failed" };
  }
}

/** Hand the browser a file. No server round trip — there is nothing to send. */
export function downloadJSON(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Erase everything, in the only order the rules permit. See the header note.
 *
 * Returns a structured result rather than throwing: `requires-recent-login` is
 * a normal outcome, not an error condition, and the caller has to be able to
 * tell it apart from a genuine failure.
 */
export async function deleteEverything(uid) {
  if (!auth?.currentUser) return { ok: false, reason: "not-signed-in" };
  let deleted = 0;
  try {
    if (db && uid) {
      const snap = await getDocs(collection(db, "users", uid, "assessments"));
      // Sequential on purpose. A batch would be faster, but a partial batch
      // failure leaves no record of how far it got, and this is the operation
      // where "it half worked" is least acceptable.
      for (const d of snap.docs) {
        await deleteDoc(d.ref);
        deleted += 1;
      }
      await deleteDoc(doc(db, "users", uid));
    }
    await auth.currentUser.delete();
    return { ok: true, deleted };
  } catch (e) {
    const code = e?.code || "delete-failed";
    console.error("[account] deleteEverything", code);
    return {
      ok: false,
      reason: code,
      // The Firestore side may already be gone; say so rather than implying
      // nothing happened.
      deleted,
      needsReauth: /requires-recent-login/.test(code),
    };
  }
}
